import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { AdminButton } from "@/components/admin/button";
import { Card, CardBody, CardHead } from "@/components/admin/card";
import { FormRow } from "@/components/admin/form";
import { AdminIcon } from "@/components/admin/icon";
import { PageHead } from "@/components/admin/page-head";
import { Tag } from "@/components/admin/tag";
import {
	useBankStatements,
	useClosePeriod,
	useImportStatement,
	useReconciliationEntries,
} from "@/hooks/use-payments";
import { fmtFCFA } from "@/lib/format";

const MATCH_LABEL: Record<string, string> = {
	auto_strict: "Match strict",
	auto_tolerance: "Tolérance",
	manual: "Manuel",
	unmatched: "Non rapproché",
};

const MATCH_CLS: Record<string, "success" | "info" | "warning" | "danger"> = {
	auto_strict: "success",
	auto_tolerance: "info",
	manual: "warning",
	unmatched: "danger",
};

export function AdminReconciliationPage() {
	const navigate = useNavigate();
	const { data: statements } = useBankStatements();
	const [selectedStmt, setSelectedStmt] = useState<string | undefined>();
	const { data: entries } = useReconciliationEntries(selectedStmt);
	const importMut = useImportStatement();
	const closePeriod = useClosePeriod();

	const [importOpen, setImportOpen] = useState(false);
	const [source, setSource] = useState("mtn_momo_pro");
	const [periodFrom, setPeriodFrom] = useState("");
	const [periodTo, setPeriodTo] = useState("");
	const [fileContent, setFileContent] = useState("");
	const [fileName, setFileName] = useState("");

	const handleFile = (file: File) => {
		setFileName(file.name);
		const reader = new FileReader();
		reader.onload = () => setFileContent(String(reader.result ?? ""));
		reader.readAsText(file);
	};

	const submitImport = async () => {
		if (!fileContent || !periodFrom || !periodTo) {
			return alert("Renseignez les périodes et le fichier.");
		}
		try {
			await importMut.mutateAsync({
				source,
				periodFrom,
				periodTo,
				fileContent,
				fileName: fileName || undefined,
			});
			setImportOpen(false);
			setFileContent("");
			setFileName("");
		} catch (e) {
			const err = e as {
				response?: { data?: { message?: string } };
				message: string;
			};
			alert(err.response?.data?.message ?? err.message);
		}
	};

	const items = entries ?? [];
	const matched = items.filter((e) => e.matchType !== "unmatched");
	const unmatched = items.filter((e) => e.matchType === "unmatched");
	const stmtList = statements ?? [];

	return (
		<>
			<PageHead
				title="Rapprochement bancaire"
				sub="Vue 3 colonnes · matching auto strict / tolérance / manuel"
				actions={
					<>
						<AdminButton variant="ghost" onClick={() => navigate("/paiements")}>
							<AdminIcon n="chevL" s={14} />
							Retour
						</AdminButton>
						<AdminButton
							variant="primary"
							onClick={() => setImportOpen((o) => !o)}
						>
							<AdminIcon n="download" s={14} />
							Importer relevé
						</AdminButton>
					</>
				}
			/>

			{importOpen && (
				<Card style={{ marginBottom: 16 }}>
					<CardHead
						title="Import CSV / OFX"
						sub="Entêtes attendues : reference, amount, date [, label]"
					/>
					<CardBody>
						<div
							style={{
								display: "grid",
								gap: 0,
								columnGap: 16,
								gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
							}}
						>
							<FormRow label="Source" required>
								<select
									className="aform-i"
									value={source}
									onChange={(e) => setSource(e.target.value)}
								>
									<option value="mtn_momo_pro">MTN MoMo Pro</option>
									<option value="om_pro">Orange Money Pro</option>
									<option value="uba">UBA</option>
									<option value="afriland">Afriland</option>
									<option value="express_union">Express Union</option>
									<option value="manual_csv">CSV manuel</option>
								</select>
							</FormRow>
							<FormRow label="Période du" required>
								<input
									type="date"
									className="aform-i"
									value={periodFrom}
									onChange={(e) => setPeriodFrom(e.target.value)}
								/>
							</FormRow>
							<FormRow label="au" required>
								<input
									type="date"
									className="aform-i"
									value={periodTo}
									onChange={(e) => setPeriodTo(e.target.value)}
								/>
							</FormRow>
						</div>
						<FormRow
							label="Fichier"
							helper={
								fileName
									? `${fileName} · ${fileContent.length.toLocaleString("fr-FR")} octets`
									: "CSV ou OFX"
							}
							required
						>
							<input
								type="file"
								accept=".csv,.txt,.ofx"
								onChange={(e) =>
									e.target.files?.[0] && handleFile(e.target.files[0])
								}
								style={{ fontSize: 13 }}
							/>
						</FormRow>
						<div
							style={{
								display: "flex",
								gap: 8,
								justifyContent: "flex-end",
								marginTop: 8,
							}}
						>
							<AdminButton variant="ghost" onClick={() => setImportOpen(false)}>
								Annuler
							</AdminButton>
							<AdminButton
								variant="primary"
								onClick={submitImport}
								disabled={importMut.isPending}
							>
								Importer
							</AdminButton>
						</div>
					</CardBody>
				</Card>
			)}

			<Card style={{ marginBottom: 16 }}>
				<CardHead
					title="Relevés importés"
					sub={`${stmtList.length} relevé(s)`}
				/>
				{stmtList.length === 0 ? (
					<CardBody>
						<div className="empty">
							Aucun relevé importé. Cliquez sur « Importer relevé » pour
							démarrer.
						</div>
					</CardBody>
				) : (
					<div className="table-wrap" style={{ margin: 0 }}>
						<table className="table">
							<thead>
								<tr>
									<th>Source</th>
									<th>Période</th>
									<th className="right">Lignes</th>
									<th className="right">Montant</th>
									<th>Statut</th>
									<th></th>
								</tr>
							</thead>
							<tbody>
								{stmtList.map((s) => (
									<tr
										key={s.id}
										style={{
											cursor: "pointer",
											background:
												selectedStmt === s.id
													? "var(--a-surface-2)"
													: undefined,
										}}
										onClick={() => setSelectedStmt(s.id)}
									>
										<td className="mono">{s.source}</td>
										<td className="muted">
											{s.periodFrom} → {s.periodTo}
										</td>
										<td className="right tn">{s.totalLines}</td>
										<td className="right tn">
											<strong>{fmtFCFA(s.totalAmount)}</strong>
										</td>
										<td>
											{s.locked ? (
												<Tag variant="neutral">Clôturée</Tag>
											) : (
												<Tag variant="warning">Ouverte</Tag>
											)}
										</td>
										<td>
											{!s.locked && (
												<AdminButton
													variant="ghost"
													onClick={(e) => {
														e.stopPropagation();
														if (
															confirm("Clôturer définitivement cette période ?")
														) {
															closePeriod.mutate(s.id);
														}
													}}
												>
													<AdminIcon n="check" s={12} />
													Clôturer
												</AdminButton>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</Card>

			{selectedStmt && (
				<div
					style={{
						display: "grid",
						gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
						gap: 16,
					}}
				>
					<Card>
						<CardHead
							title={`Banque (${items.length})`}
							sub="Lignes du relevé importé"
						/>
						<CardBody>
							{items.length === 0 ? (
								<div className="empty">Aucune ligne.</div>
							) : (
								<ul
									style={{
										paddingLeft: 16,
										fontSize: 13,
										margin: 0,
										display: "grid",
										gap: 6,
									}}
								>
									{items.map((e) => (
										<li key={e.id}>
											<strong className="mono">{e.externalReference}</strong>
											<br />
											<span className="muted">
												{fmtFCFA(e.externalAmount)} · {e.externalDate}
											</span>
										</li>
									))}
								</ul>
							)}
						</CardBody>
					</Card>
					<Card>
						<CardHead
							title={`Matched (${matched.length})`}
							sub="Auto strict + tolérance + manuel"
						/>
						<CardBody>
							{matched.length === 0 ? (
								<div className="empty">Aucun match.</div>
							) : (
								<ul
									style={{
										paddingLeft: 0,
										listStyle: "none",
										fontSize: 13,
										margin: 0,
										display: "grid",
										gap: 8,
									}}
								>
									{matched.map((e) => (
										<li
											key={e.id}
											style={{
												display: "flex",
												alignItems: "center",
												gap: 8,
												flexWrap: "wrap",
											}}
										>
											<Tag variant={MATCH_CLS[e.matchType]}>
												{MATCH_LABEL[e.matchType]}
											</Tag>
											<span className="mono">
												{e.payment?.reference ?? "—"}
											</span>
										</li>
									))}
								</ul>
							)}
						</CardBody>
					</Card>
					<Card>
						<CardHead
							title={`Écarts (${unmatched.length})`}
							sub="Nécessite rapprochement manuel"
						/>
						<CardBody>
							{unmatched.length === 0 ? (
								<div className="empty">Aucun écart 🎉</div>
							) : (
								<ul
									style={{
										paddingLeft: 16,
										fontSize: 13,
										margin: 0,
										display: "grid",
										gap: 6,
									}}
								>
									{unmatched.map((e) => (
										<li key={e.id}>
											<span className="mono">{e.externalReference}</span>
											<span className="muted">
												{" "}
												· {fmtFCFA(e.externalAmount)}
											</span>
										</li>
									))}
								</ul>
							)}
						</CardBody>
					</Card>
				</div>
			)}
		</>
	);
}
